<template>
    <SideDrawer :open="open" :title="title" :subtitle="subtitle" :busy="isSaving" @close="emit('close')">
        <form id="user-drawer-form" class="user-form" novalidate @submit.prevent="submit">
            <template v-if="mode === MODE.CREATE">
                <div class="field">
                    <label class="field__label" for="user-drawer-email">Email</label>
                    <input
                        id="user-drawer-email"
                        v-model.trim="form.email"
                        type="email"
                        autocomplete="off"
                        :class="['form-input', { 'is-invalid': errors.email }]"
                        :disabled="isSaving"
                    />
                    <p v-if="errors.email" class="field__error">{{ errors.email }}</p>
                </div>

                <PasswordField
                    input-id="user-drawer-password"
                    v-model="form.password"
                    :error="errors.password || ''"
                    :disabled="isSaving"
                >
                    <template #hint>
                        Не короче {{ MIN_PASSWORD_LENGTH }} символов. Передайте пароль пользователю безопасным способом.
                    </template>
                </PasswordField>
            </template>

            <div class="field">
                <span class="field__label">Роль</span>
                <div class="role-options" role="radiogroup">
                    <label
                        v-for="role in roles"
                        :key="role.id"
                        :class="['role-option', { 'is-selected': Number(form.role_id) === Number(role.id) }]"
                    >
                        <input v-model.number="form.role_id" type="radio" :value="role.id" :disabled="isSaving" />
                        <span class="role-option__body">
                            <span class="role-option__name">{{ role.name }}</span>
                            <span class="role-option__hint">{{ ROLE_FAMILY_HINT[roleFamily(role.code)] }}</span>
                        </span>
                    </label>
                </div>
                <p v-if="errors.role_id" class="field__error">{{ errors.role_id }}</p>
            </div>

            <div v-if="targetFamily === ROLE_FAMILY.CLIENT" class="field">
                <span class="field__label">Режим доступа</span>
                <div class="mode-options" role="radiogroup">
                    <label :class="['mode-option', { 'is-selected': form.access_mode === ACCESS_MODE.FULL }]">
                        <input v-model="form.access_mode" type="radio" :value="ACCESS_MODE.FULL" :disabled="isSaving" />
                        Полный доступ
                    </label>
                    <label :class="['mode-option', { 'is-selected': form.access_mode === ACCESS_MODE.READONLY }]">
                        <input v-model="form.access_mode" type="radio" :value="ACCESS_MODE.READONLY" :disabled="isSaving" />
                        Только просмотр
                    </label>
                </div>
                <p v-if="errors.access_mode" class="field__error">{{ errors.access_mode }}</p>
                <p v-else-if="form.access_mode === ACCESS_MODE.READONLY" class="field__hint">
                    Пользователь увидит данные, но не сможет менять структуру, кампании и креативы.
                </p>
            </div>

            <div v-if="scopeKind" class="field">
                <span class="field__label">{{ scopeKind === 'partner' ? 'Партнёры' : 'Рекламодатели' }}</span>
                <ScopePicker
                    v-if="scopeKind === 'partner'"
                    v-model="form.partner_ids"
                    kind="partner"
                    :partners="partners"
                    :advertisers="advertisers"
                    :error="errors.partner_ids"
                    :disabled="isSaving"
                />
                <ScopePicker
                    v-else
                    v-model="form.advertiser_ids"
                    kind="advertiser"
                    :partners="partners"
                    :advertisers="advertisers"
                    :error="errors.advertiser_ids"
                    :disabled="isSaving"
                />
                <p v-if="foreignScopeCount" class="field__hint">
                    Вне вашей зоны у пользователя есть ещё доступы
                    ({{ foreignScopeCount }} {{ pluralize(foreignScopeCount, scopeKind === 'partner' ? PLURAL.PARTNER : PLURAL.ADVERTISER) }}) — они сохранятся.
                </p>
            </div>
            <p v-else-if="targetFamily === ROLE_FAMILY.ADMIN" class="field__hint">
                Администратору доступы не назначаются — он видит всё.
            </p>

            <div v-if="consequences.length" class="consequences" role="note">
                <p class="consequences__title">После сохранения</p>
                <ul>
                    <li v-for="item in consequences" :key="item">{{ item }}</li>
                </ul>
            </div>

            <p v-if="generalError" class="form-error" role="alert">{{ generalError }}</p>
        </form>

        <template #footer>
            <button type="button" class="btn-secondary" :disabled="isSaving" @click="emit('close')">Отмена</button>
            <button type="submit" form="user-drawer-form" class="btn-primary" :disabled="!canSubmit || isSaving">
                {{ isSaving ? 'Сохранение…' : submitLabel }}
            </button>
        </template>
    </SideDrawer>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import axios from 'axios';
import SideDrawer from '../../ui/SideDrawer.vue';
import PasswordField from './PasswordField.vue';
import ScopePicker from './ScopePicker.vue';
import { childrenOf } from './buildUserTree';
import { ROLE_FAMILY, ROLE_FAMILY_HINT, canFamilyOwn, roleFamily } from '../../../constants/roles';
import { ACCESS_MODE, DRAWER_MODE as MODE, MIN_PASSWORD_LENGTH } from '../../../constants/users';
import { extractErrorMessage, extractFieldErrors } from '../../../utils/httpErrors';
import { PLURAL, pluralize } from '../../../utils/plural';

const props = defineProps({
    open: { type: Boolean, required: true },
    mode: { type: String, required: true, validator: value => Object.values(MODE).includes(value) },
    user: { type: Object, default: null },
    actor: { type: Object, required: true },
    users: { type: Array, required: true },
    roles: { type: Array, required: true },
    partners: { type: Array, required: true },
    advertisers: { type: Array, required: true },
});

const emit = defineEmits(['close', 'saved']);

const emptyForm = () => ({
    email: '',
    password: '',
    role_id: null,
    access_mode: ACCESS_MODE.FULL,
    partner_ids: [],
    advertiser_ids: [],
});

const form = reactive(emptyForm());
const errors = ref({});
const generalError = ref('');
const isSaving = ref(false);

const visiblePartnerIds = computed(() => new Set(props.partners.map(partner => Number(partner.id))));
const visibleAdvertiserIds = computed(() => new Set(props.advertisers.map(advertiser => Number(advertiser.id))));

const resetForm = () => {
    Object.assign(form, emptyForm());
    errors.value = {};
    generalError.value = '';

    if (props.mode === MODE.CREATE) {
        if (props.roles.length === 1) form.role_id = props.roles[0].id;
        return;
    }

    const user = props.user;
    form.role_id = props.roles.some(role => Number(role.id) === Number(user?.role?.id)) ? user.role.id : null;
    form.access_mode = user?.access_mode === ACCESS_MODE.READONLY ? ACCESS_MODE.READONLY : ACCESS_MODE.FULL;

    if (props.mode === MODE.EDIT) {
        form.partner_ids = (user?.scope?.partner_ids || []).map(Number).filter(id => visiblePartnerIds.value.has(id));
        form.advertiser_ids = (user?.scope?.advertiser_ids || []).map(Number).filter(id => visibleAdvertiserIds.value.has(id));
    }
};

watch(() => props.open, (isOpen) => {
    if (isOpen) resetForm();
});

const selectedRole = computed(() => props.roles.find(role => Number(role.id) === Number(form.role_id)) || null);
const currentFamily = computed(() => roleFamily(props.user?.role?.code));
const targetFamily = computed(() => roleFamily(selectedRole.value?.code));

const scopeKind = computed(() => {
    if (targetFamily.value === ROLE_FAMILY.AGENCY) return 'partner';
    if (targetFamily.value === ROLE_FAMILY.CLIENT) return 'advertiser';
    return null;
});

const selectedScope = computed(() => (scopeKind.value === 'partner' ? form.partner_ids : form.advertiser_ids));

/** Гранты, выданные вне зоны актора: агентство их не видит, бэкенд их не трогает. */
const foreignScopeCount = computed(() => {
    if (props.mode !== MODE.EDIT || !scopeKind.value || currentFamily.value !== targetFamily.value) return 0;
    const [ids, visible] = scopeKind.value === 'partner'
        ? [props.user?.scope?.partner_ids || [], visiblePartnerIds.value]
        : [props.user?.scope?.advertiser_ids || [], visibleAdvertiserIds.value];
    return ids.filter(id => !visible.has(Number(id))).length;
});

const title = computed(() => ({
    [MODE.CREATE]: 'Новый пользователь',
    [MODE.EDIT]: 'Редактирование пользователя',
    [MODE.RESTORE]: 'Восстановление пользователя',
}[props.mode]));

const subtitle = computed(() => (props.mode === MODE.CREATE ? '' : props.user?.email || ''));

const submitLabel = computed(() => ({
    [MODE.CREATE]: 'Создать',
    [MODE.EDIT]: 'Сохранить',
    [MODE.RESTORE]: 'Восстановить',
}[props.mode]));

const canSubmit = computed(() => {
    if (!selectedRole.value) return false;
    if (props.mode === MODE.CREATE && (!form.email || form.password.length < MIN_PASSWORD_LENGTH)) return false;
    return !scopeKind.value || selectedScope.value.length > 0;
});

const consequences = computed(() => {
    const items = [];

    if (props.mode === MODE.RESTORE) {
        items.push('Пользователь снова сможет войти с прежним паролем.');
        items.push('Клиенты, переданные другим при деактивации, останутся у текущих владельцев.');
        return items;
    }

    if (props.mode !== MODE.EDIT || !props.user || !targetFamily.value) return items;

    if (currentFamily.value !== targetFamily.value) {
        items.push(targetFamily.value === ROLE_FAMILY.ADMIN
            ? 'Все доступы пользователя будут сняты — администратору они не нужны.'
            : 'Прежние доступы будут заменены выбранными выше.');

        const orphans = childrenOf(props.users, props.user.id)
            .filter(child => !canFamilyOwn(targetFamily.value, roleFamily(child.role?.code)));
        if (orphans.length) {
            items.push(`Подчинённые (${orphans.length} ${pluralize(orphans.length, PLURAL.USER)}) перейдут под ваше управление и сохранят доступы.`);
        }
    }

    if (form.access_mode !== props.user.access_mode && targetFamily.value === ROLE_FAMILY.CLIENT) {
        items.push(form.access_mode === ACCESS_MODE.READONLY
            ? 'Пользователь потеряет возможность что-либо изменять.'
            : 'Пользователь снова сможет создавать и редактировать данные.');
    }

    return items;
});

const buildPayload = () => {
    const payload = {
        role_id: Number(form.role_id),
        access_mode: targetFamily.value === ROLE_FAMILY.CLIENT ? form.access_mode : ACCESS_MODE.FULL,
    };
    if (scopeKind.value === 'partner') payload.partner_ids = form.partner_ids.map(Number);
    if (scopeKind.value === 'advertiser') payload.advertiser_ids = form.advertiser_ids.map(Number);
    if (props.mode === MODE.CREATE) {
        payload.email = form.email;
        payload.password = form.password;
    }
    return payload;
};

const request = (payload) => {
    if (props.mode === MODE.CREATE) return axios.post('/administration/users', payload);
    if (props.mode === MODE.RESTORE) return axios.post(`/administration/users/${props.user.id}/restore`, payload);
    return axios.put(`/administration/users/${props.user.id}`, payload);
};

const successMessage = (user) => ({
    [MODE.CREATE]: `Пользователь ${user.email} создан`,
    [MODE.EDIT]: 'Изменения сохранены',
    [MODE.RESTORE]: `Пользователь ${user.email} восстановлен`,
}[props.mode]);

const submit = async () => {
    if (!canSubmit.value || isSaving.value) return;

    isSaving.value = true;
    errors.value = {};
    generalError.value = '';

    try {
        const { data } = await request(buildPayload());
        emit('saved', {
            user: data.user,
            reassignedIds: data.reassigned_user_ids || [],
            message: successMessage(data.user),
        });
    } catch (error) {
        errors.value = extractFieldErrors(error);
        if (!Object.keys(errors.value).length || errors.value.user) {
            generalError.value = errors.value.user || extractErrorMessage(error);
        }
    } finally {
        isSaving.value = false;
    }
};
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;
@use '../../../../scss/pages/admin/admin-form';

@include admin-form.fields;

.user-form {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;
}

.role-options {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
}

.role-option {
    display: flex;
    align-items: flex-start;
    gap: $spacing-md;
    padding: $spacing-md;
    border: 1px solid $admin-border-color;
    border-radius: $radius-md;
    cursor: pointer;

    &:hover { border-color: $admin-border-hover; }

    &.is-selected {
        border-color: $admin-primary-color;
        background: #f0f7ff;
    }

    input { margin-top: 3px; }
}

.role-option__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.role-option__name {
    font-weight: 600;
    color: $admin-text-primary;
}

.role-option__hint {
    font-size: $font-size-sm;
    color: $admin-text-muted;
}

.mode-options {
    display: inline-flex;
    gap: $spacing-sm;
}

.mode-option {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    padding: $spacing-sm $spacing-md;
    border: 1px solid $admin-border-color;
    border-radius: $radius-md;
    font-size: $font-size-base;
    cursor: pointer;

    &.is-selected {
        border-color: $admin-primary-color;
        background: #f0f7ff;
    }
}

.consequences {
    padding: $spacing-md $spacing-lg;
    border-radius: $radius-md;
    border: 1px solid #fde68a;
    background: $admin-warning-bg;
    font-size: $font-size-sm;
    color: #78350f;

    ul {
        margin: 0;
        padding-left: 1.1rem;
    }

    li + li { margin-top: $spacing-xs; }
}

.consequences__title {
    margin: 0 0 $spacing-xs;
    font-weight: 600;
}
</style>
